import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import {
  ApiListResponse,
  CoesProviderStatus,
  ComuneItem,
  CountryItem,
  PaginatedProvidersResponse,
  Provider,
  ProviderStatus,
  ProvinceItem,
} from '../interfaces/providers-interface';

export interface ApiConfig {
  apiUrl: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  private http = inject(HttpClient);

  private providersSubject = new BehaviorSubject<Provider[]>([]);
  public providers$ = this.providersSubject.asObservable();

  private totalProviderSubject = new BehaviorSubject<number>(0);
  public totalProviders$ = this.totalProviderSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private config: ApiConfig | null = null;

  configure(config: ApiConfig): void {
    this.config = config;
  }

  /**
   * Carga providers sin enviar Bearer token
   */
  getProviders(
    page = 1,
    limit = 10,
    status: ProviderStatus = 'all',
    search = '',
    q: CoesProviderStatus = ''
  ): Observable<PaginatedProvidersResponse> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers`;

    this.loadingSubject.next(true);

    let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    if (search.trim()) params = params.set('q', search.trim());
    if (status && status !== 'all') {
      params = params.set('status', status);
    }
    if (q !== null && q !== '' && q !== undefined) {
      params = params.set('q', q.trim());
    }

    return this.http.get<PaginatedProvidersResponse>(url, { params }).pipe(
      tap((response) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.providersSubject.next(response.data);
          this.totalProviderSubject.next(response.total || 0);
        } else {
          this.providersSubject.next([]);
          this.totalProviderSubject.next(0);
        }
      }),
      catchError((error) => {
        console.error('[ProviderService] Error loading providers:', error);
        this.providersSubject.next([]);
        this.totalProviderSubject.next(0);
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );
  }

  /** Resto de métodos utilizan HttpClient con interceptor */
  getProviderById(id: string): Observable<Provider> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/${id}/`;
    return this.http.get<Provider>(url);
  }

  createProvider(provider: Partial<Provider>): Observable<Provider> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/`;
    return this.http.post<Provider>(url, provider);
  }

  updateProvider(id: number, provider: Partial<Provider>): Observable<Provider> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/${id}/`;
    return this.http.put<Provider>(url, provider);
  }

  deleteProvider(id: number): Observable<void> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/${id}/`;
    return this.http.delete<void>(url);
  }

  getCountries(q = ''): Observable<ApiListResponse<CountryItem>> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/countries/`;

    let params = new HttpParams();
    if (q.trim()) params = params.set('q', q.trim());

    return this.http.get<ApiListResponse<CountryItem>>(url, { params });
  }

  getProvinces(q = ''): Observable<ApiListResponse<ProvinceItem>> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/provinces/`;

    let params = new HttpParams();
    if (q.trim()) params = params.set('q', q.trim());

    return this.http.get<ApiListResponse<ProvinceItem>>(url, { params });
  }

  getCommunes(q = ''): Observable<ApiListResponse<ComuneItem>> {
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/api/users/providers/comunes/`;

    let params = new HttpParams();
    if (q.trim()) params = params.set('q', q.trim());

    return this.http.get<ApiListResponse<ComuneItem>>(url, { params });
  }

  /**
   * Método auxiliar para recargar la lista de providers
   */
  private refreshProviders(): void {
    this.getProviders(1, 10).subscribe({
      next: () => console.log('[ProviderService] Providers refreshed'),
      error: (error) => console.error('[ProviderService] Error refreshing providers:', error),
    });
  }

  private getApiUrl(): string {
    if (!this.config || !this.config.apiUrl) {
      throw new Error('ProviderService no está configurado. Llama a configure() primero.');
    }
    return this.config.apiUrl;
  }
}
